import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { PUBLIC_BASE } from "../lib/api";

const STATUS_META = {
  pending: { label: "Pending", color: "text-amber-300 bg-amber-500/15 border-amber-400/40" },
  confirmed: { label: "Confirmed", color: "text-emerald-300 bg-emerald-500/15 border-emerald-400/40" },
  cancelled: { label: "Cancelled", color: "text-zinc-500 bg-white/5 border-white/10 line-through" },
};

/**
 * Realtor-facing showings calendar embedded on the public dashboard.
 * Shows only upcoming + recently-past bookings; auto-hides when there are
 * none so the panel doesn't add visual noise. Token-gated like the rest of
 * the public-but-private dashboard endpoints.
 */
export default function BookingsPanel({ token, brand }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${PUBLIC_BASE}/api/share/dashboards/${token}/bookings`);
      setBookings(r.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  const setStatus = async (bid, status) => {
    try {
      await axios.patch(
        `${PUBLIC_BASE}/api/share/dashboards/${token}/bookings/${bid}`,
        { status }
      );
      setBookings((arr) => arr.map((b) => (b.id === bid ? { ...b, status } : b)));
    } catch {}
  };

  if (bookings.length === 0 && !loading) return null;

  const upcoming = bookings.filter((b) => b.status !== "cancelled");
  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  return (
    <div
      className="rounded-2xl border bg-[#0A0A0A]"
      style={{ borderColor: brand ? `${brand}30` : "rgb(255 255 255 / 0.1)" }}
      data-testid="bookings-panel"
    >
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
        data-testid="bookings-panel-toggle"
      >
        <Icons.CalendarCheck size={16} style={{ color: brand || "#a1a1aa" }} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.4em]" style={{ color: brand || "#71717a" }}>
            Upcoming showings
          </div>
          <div className="font-display text-lg text-white">
            {upcoming.length} {upcoming.length === 1 ? "showing" : "showings"} scheduled
            {pendingCount > 0 && (
              <span className="text-amber-300 text-sm ml-2 font-sans">
                · {pendingCount} pending confirmation
              </span>
            )}
          </div>
        </div>
        <Icons.ChevronDown
          size={18}
          className={`text-zinc-500 transition-transform ${collapsed ? "" : "rotate-180"}`}
        />
      </button>

      {!collapsed && (
        <div className="px-5 pb-5 space-y-2">
          {bookings.map((b) => {
            const d = new Date(b.slot);
            const statusMeta = STATUS_META[b.status || "pending"];
            return (
              <div
                key={b.id}
                className="bg-white/[0.02] border border-white/10 rounded-xl p-3 flex items-start gap-3"
                data-testid={`booking-${b.id}`}
              >
                {/* Date column */}
                <div
                  className="shrink-0 w-14 text-center rounded-lg py-2 border"
                  style={{
                    borderColor: brand ? `${brand}40` : "rgb(255 255 255 / 0.1)",
                    color: brand || "#fff",
                  }}
                >
                  <div className="text-[9px] uppercase tracking-widest opacity-70">
                    {d.toLocaleDateString(undefined, { weekday: "short" })}
                  </div>
                  <div className="font-display text-2xl leading-none">{d.getDate()}</div>
                  <div className="text-[9px] uppercase tracking-widest opacity-70">
                    {d.toLocaleDateString(undefined, { month: "short" })}
                  </div>
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium text-white text-sm">
                      {d.toLocaleTimeString(undefined, {
                        hour: "numeric", minute: "2-digit",
                      })}
                    </span>
                    <span className="text-xs text-zinc-300">{b.name || "Visitor"}</span>
                    {b.listing_address && (
                      <span className="text-[11px] text-zinc-500">
                        · {b.listing_address}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-zinc-400 mt-0.5">
                    {b.email && (
                      <a href={`mailto:${b.email}`} className="hover:text-white inline-flex items-center gap-1">
                        <Icons.Mail size={10} /> {b.email}
                      </a>
                    )}
                    {b.phone && (
                      <a href={`tel:${b.phone}`} className="hover:text-white inline-flex items-center gap-1">
                        <Icons.Phone size={10} /> {b.phone}
                      </a>
                    )}
                  </div>
                  {b.message && (
                    <div className="text-[12px] text-zinc-400 mt-1.5 italic">
                      "{b.message}"
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mt-2">
                    {Object.entries(STATUS_META).map(([id, m]) => {
                      const active = (b.status || "pending") === id;
                      return (
                        <button
                          key={id}
                          onClick={() => setStatus(b.id, id)}
                          className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border transition-all ${
                            active ? m.color : "text-zinc-500 border-white/10 hover:text-white"
                          }`}
                          data-testid={`booking-${b.id}-status-${id}`}
                        >
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
